import type { SettableOfferRequestReportStatus } from '@houzy/schemas/offer-request-management';
import type { PartnerCockpitOfferRequest } from '@houzy/schemas/partner-cockpit/offer-requests';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { reimbursementOutcome } from '../../reimbursement';
import {
    formatCreditsLabel,
    offerRequestProjectSizeLabel,
    offerRequestLeadStatusLabel,
    offerRequestTimingLabel,
    offerRequestTitle,
} from '../labels';
import { isOfferRequestStatusLocked } from '../offerRequestCapabilities';
import { formatOfferRequestSubtitle } from '../utils/formatOfferRequestSubtitle';
import { OfferRequestStatusSelect } from './OfferRequestStatusSelect';
import { ReimbursementStatusChip } from './ReimbursementStatusChip';

export type OpenOfferRequestCardProps = {
    offerRequest: PartnerCockpitOfferRequest;
    onClick?: () => void;
    onStatusChange?: (status: SettableOfferRequestReportStatus) => void;
    // Forwarded clock anchor so stories/tests keep relative times deterministic.
    now?: Date;
};

export function OpenOfferRequestCard({
    offerRequest,
    onClick,
    onStatusChange,
    now,
}: OpenOfferRequestCardProps): ReactElement {
    const { t, i18n } = useTranslation();
    const settled = offerRequest.leadStatus === 'engaged';
    const outcome = reimbursementOutcome(offerRequest.reimbursement);
    const leadStatusBadge = offerRequestLeadStatusLabel(t, offerRequest.leadStatus);
    const title = offerRequestTitle(
        t,
        offerRequest.title,
        offerRequest.workCategoryKeys,
        offerRequest.referenceId,
        i18n.language,
    );

    return (
        <Card variant="outlined" sx={{ position: 'relative' }}>
            <CardActionArea
                onClick={onClick}
                disabled={onClick === undefined}
                aria-label={t('OPEN_REQUESTS.CARD.OPEN_DETAIL', { title })}>
                <CardHeader
                    title={
                        // Reversed on mobile so the badge sits above the title instead of beside it.
                        <Stack
                            direction={{ xs: 'column-reverse', md: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            // A long work-category title would otherwise push the badge under the status select.
                            sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                                    overflowWrap: 'anywhere',
                                }}>
                                {title}
                            </Typography>
                            {leadStatusBadge !== undefined && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={leadStatusBadge}
                                    sx={{ flexShrink: 0 }}
                                />
                            )}
                        </Stack>
                    }
                    subheader={formatOfferRequestSubtitle(t, offerRequest, now)}
                    slotProps={{
                        content: { sx: { minWidth: 0 } },
                        subheader: { variant: 'body2', sx: { overflowWrap: 'anywhere' } },
                    }}
                    sx={{ pr: { xs: 2, md: 18 } }}
                />
                <CardContent sx={{ pt: 0 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between">
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip size="small" label={offerRequestTimingLabel(t, offerRequest.timeConstraint)} />
                            {settled
                                ? offerRequest.credits !== undefined && (
                                      <Chip size="small" label={formatCreditsLabel(t, offerRequest.credits)} />
                                  )
                                : offerRequest.projectSize !== undefined && (
                                      <Chip
                                          size="small"
                                          label={offerRequestProjectSizeLabel(t, offerRequest.projectSize)}
                                      />
                                  )}
                        </Stack>
                        {outcome && (
                            <ReimbursementStatusChip
                                outcome={outcome}
                                reimbursedCredits={offerRequest.reimbursedCredits}
                            />
                        )}
                    </Stack>
                </CardContent>
            </CardActionArea>

            {/* Pinned to the header on desktop; on mobile it flows into its own row under the card body. */}
            <Box
                sx={{
                    position: { xs: 'static', md: 'absolute' },
                    top: 16,
                    right: 16,
                    px: { xs: 2, md: 0 },
                    pb: { xs: 2, md: 0 },
                }}>
                <OfferRequestStatusSelect
                    status={offerRequest.status}
                    disabled={isOfferRequestStatusLocked(offerRequest)}
                    tone="neutral"
                    onStatusChange={onStatusChange}
                />
            </Box>
        </Card>
    );
}
